import { action, autorun, computed, observable, runInAction } from "mobx";
import { Observer } from "mobx-react";
import { userInfo } from "os";
import { Button } from "primereact/button";
import React, { useContext, useEffect } from "react";
import { QueryClient, useQueryClient } from "react-query";
import styled from "styled-components";
import { candidateAPI } from "../../../api/candidate";
import { GetDetailsForCandidateResponse } from "../../../api/candidate/getDetailsForCandidate";
import { getLocStore, useLocStore } from "../../../wsLocalization";
import { LocContext } from "../../../wsLocalization/LocContext";
import { Store2 } from "../../../WSUtils/Store";
import { useLatestQuery } from "../../../WSUtils/useLatestQuery";
import { UserInfo } from "../../../WSUtils/UserInfo";
import { UserInfoContext } from "../../../WSUtils/UserInfoContext";
import { useStore2 } from "../../../WSUtils/useStore";
import { WSFormGrid } from "../../../WSUtils/WSFormGrid";
import { sentryAssertData } from "../../../WSUtils/wsSentry";
import { wsSentryReport } from "../../../WSUtils/wsSentryReport";
import { CVEditContext, CVEditContextStore } from "../CVEditContext";
import {
  CVEditBaseField,
  cveField,
  OnRenderCvEditComponent,
  OnRenderCvEditContainer,
} from "../Fields/CVEditBaseField";
import { defaultCvEditTextFieldDef } from "../Fields/defaultCvEditTextField";

export const CVEditAddressSegment = () => {
  const L = useLocStore(LocContext.CV);
  const cvEditContext = useContext(CVEditContext);
  const userInfo = useContext(UserInfoContext);

  const store = useStore2(
    "CVEditAddressSegmentStore",
    (dt) =>
      new CVEditAddressSegmentStore({
        name: "CVEditAddressSegmentStore",
        parentDt: dt,
        useHooks: (t: CVEditAddressSegmentStore) => {
          // eslint-disable-next-line
          t.userInfo = useContext(UserInfoContext);
          // eslint-disable-next-line
          t.queryClient = useQueryClient();
          // eslint-disable-next-line
          t.cvEditContext = useContext(CVEditContext);
        },
      })
  );

  const { data: details } = useLatestQuery(
    ["candidateAPI.getDetailsForCandidate"],
    () =>
      candidateAPI.getDetailsForCandidate({
        userInfo,
      })
  );

  sentryAssertData([details], { userInfo });

  useEffect(
    action(() => {
      store.details = details;
    }),
    [details]
  );

  return (
    <Observer
      render={() => (
        <CVEditAddressSegmentStyles className="cve-address-segment cve-segment">
          <div className="cvp-h2 cve-header">
            {L.id("403c72aea860").loc("Adresse")}
          </div>
          <WSFormGrid horizontal>
            <CVEditBaseField
              def={store.zipCodeField}
              containerProps={{ style: { flexGrow: 1 } }}
            />
            <CVEditBaseField
              def={store.cityField}
              containerProps={{ style: { flexGrow: 2 } }}
            />
          </WSFormGrid>
          <CVEditBaseField def={store.addressField} />
          <CVEditBaseField def={store.postAddressField} />
        </CVEditAddressSegmentStyles>
      )}
    />
  );
};

const CVEditAddressSegmentStyles = styled(WSFormGrid)`
  .cve-show-postal-address-button {
    flex-shrink: 0;
  }
`;

export class CVEditAddressSegmentStore extends Store2<any> {
  declare userInfo: UserInfo;
  declare queryClient: QueryClient;
  declare cvEditContext: CVEditContextStore;

  @observable details: GetDetailsForCandidateResponse;

  getFieldDependencies = () => ({
    cvEditContext: this.cvEditContext,
    queryClient: this.queryClient,
    userInfo: this.userInfo,
    dt: this.dt,
  });

  @computed get L() {
    return getLocStore(this.userInfo, LocContext.CV);
  }

  @computed get showPostalAddress() {
    if (this.showPostalAddressHint) return true;

    return !!this.postAddressField?.store?.value;
  }

  @observable showPostalAddressHint = (() => {
    this.dt.disposers.push(
      autorun(() => {
        if (this.showPostalAddress) {
          runInAction(() => {
            this.showPostalAddressHint = true;
          });
        }
      })
    );

    return false;
  })();

  @observable zipCodeField = cveField((getValueVersioner) => {
    const fieldName = "postnummer";
    const userInfo = this.userInfo;

    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName,
      id: "ZipCode",
      label: "Postnummer",
      maxLength: 10,
      ...this.getFieldDependencies(),
      mutate: async (newValue: string) => {
        const zipCode = newValue;
        let city: string = null;

        if (zipCode.length === 4) {
          try {
            let r = await userInfo.fullAPI.placesAPI.getCityForZipCode({
              userInfo,
              zipCode,
            });

            city = r.item?.sted ?? null;
          } catch (e) {
            wsSentryReport({
              simpleMessage: "",
              showDialog: false,
              userInfo,
              tag: "placesAPI.getCityForZipCode",
              zipCode,
            });
          }
        }

        const fields = [
          {
            fieldName: fieldName,
            fieldValue: newValue,
          },
        ];

        if (city) {
          fields.push({
            fieldName: "poststed",
            fieldValue: city,
          });
        }

        return await userInfo.fullAPI.candidateAPI.updateCandidate({
          showSentryDialogOnError: true,
          userInfo,
          fields,
        });
      },
    });

    return def;
  });

  @observable cityField = cveField((getValueVersioner) => {
    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName: "poststed",
      id: "City",
      label: "Poststed",
      maxLength: 100,
      ...this.getFieldDependencies(),
    });

    return def;
  });

  @observable addressField = cveField((getValueVersioner) => {
    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName: "besoksadresse",
      id: "Address",
      label: "Adresse",
      maxLength: 100,
      ...this.getFieldDependencies(),
    });

    const getLabel = () => {
      if (this.showPostalAddress)
        return this.L.id("ebd68b30e3fc").loc("Besøksadresse");

      return this.L.id("4e2db63cbbad").loc("Adresse");
    };

    const onRenderComponent: OnRenderCvEditComponent = ({
      defaultRender,
      def,
    }) => (
      <Observer
        render={() => (
          <>
            {!this.showPostalAddress && (
              <div className="p-inputgroup">
                {defaultRender({ def })}
                <Button
                  className="p-button-secondary cve-show-postal-address-button"
                  label={this.L.id("294477bcc208").loc("Postadresse")}
                  icon="pi pi-plus"
                  onClick={(evt) => {
                    evt.preventDefault();

                    this.showPostalAddressHint = true;

                    return false;
                  }}
                />
              </div>
            )}

            {!!this.showPostalAddress && defaultRender({ def })}
          </>
        )}
      />
    );

    return { ...def, getLabel, onRenderComponent };
  });

  @observable postAddressField = cveField((getValueVersioner) => {
    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName: "postadresse",
      id: "PostAddress",
      label: "Postadresse",
      maxLength: 100,
      ...this.getFieldDependencies(),
    });

    const onRenderContainer: OnRenderCvEditContainer = (props) => (
      <Observer
        render={() => {
          const hideIt = !this.showPostalAddress;

          return (
            <div
              {...props.containerProps}
              style={{
                ...(props.containerProps?.style || {}),
                display: hideIt ? "none" : undefined,
              }}
            >
              {props.children}
            </div>
          );
        }}
      />
    );

    return { ...def, onRenderContainer };
  });
}
